import { useState } from 'react';
import { cn } from '../../../../../lib/utils';
import { secondaryText } from '../../../../../styling/stylingHelper';
import { setAICheckerWorkState, type AICheckerTrace } from '../../../../../services/admin/adminService';
import { openQuantityFlagCount } from './quantityFlags/qtyFlagGrouping';
import type { TFn } from '../types';

// Status badge for one audited email (was it stored, and as what).
export function statusBadge(trace: AICheckerTrace, t: TFn) {
  if (trace.matchedIn === 'order') {
    return <span className="inline-block text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{t('adminTesting.review.statusOrder')}</span>;
  }
  if (trace.matchedIn === 'revenue') {
    return <span className="inline-block text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300">{t('adminTesting.review.statusRevenue')}</span>;
  }
  return <span className="inline-block text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{t('adminTesting.review.statusNotProcessed')}</span>;
}

// Check/outcome cell — did the audit pass, or what triage bucket was it.
// Short, specific label for a revenue row's primary issue (full list shown in the detail card).
// Matches the stable issue strings emitted server-side by the revenue checks.
function revenueIssueLabel(issues: string[], t: TFn): string {
  if (issues.some((i) => i.startsWith('Missing revenue section'))) return t('adminTesting.review.checkMissingSection');
  if (issues.some((i) => i.startsWith('Unmapped revenue product'))) return t('adminTesting.review.checkUnmappedProduct');
  if (issues.some((i) => i.includes('no categories') || i.includes('€0 across'))) return t('adminTesting.review.checkRevenueEmpty');
  return t('adminTesting.review.checkRevenueData');
}

// `wrap` lets the mobile card flow the outcome text over several lines; the desktop table keeps nowrap.
export function checkCell(trace: AICheckerTrace, t: TFn, wrap = false) {
  const parts: React.ReactNode[] = [];
  // A manual mark leaves the checker's own verdict untouched, so say so up front — otherwise the row
  // reads as "safe skip" while sitting in the issue queue.
  if (trace.manualIssue) {
    parts.push(
      <span key="m" title={t('adminTesting.review.manualIssueHint')} className="text-red-600 dark:text-red-400 font-semibold">
        ⚠ {t('adminTesting.review.manualIssue')} ·{' '}
      </span>,
    );
  }
  if (trace.matchedIn === 'order') {
    if (trace.emailVerdict === 'correct') parts.push(<span key="v" className="text-emerald-600 dark:text-emerald-400">✓ {t('adminTesting.review.checkCorrect')}</span>);
    else if (trace.emailVerdict === 'wrong') parts.push(<span key="v" className="text-red-600 dark:text-red-400">✗ {t('adminTesting.review.checkWrong')}</span>);
    else if (trace.emailVerdict === 'inconclusive') {
      parts.push(
        <span key="v" title={t('adminTesting.review.checkInconclusiveHint')} className="text-slate-500 dark:text-slate-400">
          {t('adminTesting.review.checkInconclusive')}
        </span>,
      );
    } else parts.push(<span key="v" title={t('adminTesting.review.checkNotAuditedHint')} className={secondaryText}>– {t('adminTesting.review.checkNotAudited')}</span>);
    // Count only UNRESOLVED quantity flags so the badge reflects flags marked done in the Issues tab.
    const totalQty = (trace.quantityFlags ?? []).length || trace.quantityFlagCount;
    const openQty = openQuantityFlagCount(trace);
    if (openQty > 0) {
      parts.push(<span key="q" className="text-orange-600 dark:text-orange-400"> · {t('adminTesting.review.checkQtyFlags', { count: openQty })}</span>);
    } else if (totalQty > 0) {
      parts.push(<span key="q" className="text-emerald-600 dark:text-emerald-400"> · {t('adminTesting.review.checkQtyFlagsDone', { count: totalQty })}</span>);
    }
    // Factuur verification badge — only on factuur emails for factuur-supported platforms.
    if (trace.factuurExpected) {
      if (trace.factuurCreated) {
        parts.push(
          <span key="f" title={t('adminTesting.review.factuurCreatedHint', { count: trace.factuurOrdersCount ?? 0 })} className="text-emerald-600 dark:text-emerald-400"> · {t('adminTesting.review.factuurCreated')}</span>,
        );
      } else {
        parts.push(
          <span key="f" title={t('adminTesting.review.factuurMissingHint')} className="text-red-600 dark:text-red-400"> · {t('adminTesting.review.factuurMissing')}</span>,
        );
      }
    }
  } else if (trace.matchedIn === 'revenue') {
    if (trace.hasIssue) {
      parts.push(<span key="v" className="text-red-600 dark:text-red-400">⚠ {revenueIssueLabel(trace.emailIssues, t)}</span>);
      if (trace.emailIssues.length > 1) parts.push(<span key="n" className="text-red-600 dark:text-red-400"> +{trace.emailIssues.length - 1}</span>);
    } else {
      parts.push(<span key="v" className="text-emerald-600 dark:text-emerald-400">✓ {t('adminTesting.review.checkAllGood')}</span>);
    }
  } else if (trace.triageCategory === 'new_supplier') {
    parts.push(<span key="v" className="text-red-600 dark:text-red-400">{t('adminTesting.review.bucketNewSuppliers')}</span>);
  } else if (trace.triageCategory === 'needs_attention') {
    parts.push(<span key="v" className="text-red-600 dark:text-red-400">{t('adminTesting.review.bucketNeedsAttention')}</span>);
  } else if (trace.triageCategory === 'known') {
    parts.push(<span key="v" className="text-amber-600 dark:text-amber-400">★ {t('adminTesting.review.checkKnown')}</span>);
  } else {
    parts.push(<span key="v" className={secondaryText}>{t('adminTesting.review.statSafeSkip')}{trace.triageSkipKind ? ` (${trace.triageSkipKind})` : ''}</span>);
  }
  return <span className={wrap ? 'whitespace-normal' : 'whitespace-nowrap'}>{parts}</span>;
}

export function DoneToggle({ trace, workState, onChange, t }: { trace: AICheckerTrace; workState: string; onChange: (ws: string) => void; t: TFn }) {
  const [busy, setBusy] = useState(false);
  const done = workState !== 'open';
  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const next = done ? 'open' : 'resolved';
      await setAICheckerWorkState(trace.id, next as 'open' | 'resolved');
      onChange(next);
    } catch (err) {
      console.error('Failed to update work state', err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={toggle}
      disabled={busy}
      className={cn('px-2 py-1 rounded-md text-xs transition-colors disabled:opacity-50 whitespace-nowrap',
        done
          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600')}
    >
      {done ? '✓ ' + t('adminTesting.review.markedDone') : t('adminTesting.review.markDone')}
    </button>
  );
}
